import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {Upload, Save, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Type} from 'lucide-react'

interface SiteImagesManagerProps {
  onClose: () => void
  embedded?: boolean
}

interface SiteImage {
  _id: string
  section: string
  imageUrl: string
  altText: string
  caption?: string
  active: boolean
  order: number
  createdAt: string
  updatedAt: string
}

const SECTION_LABELS: Record<string, string> = {
  hero: "✨ Rayonner - Slider (multiple images)",
  hero_quiz: "📝 Images avant bouton Quiz (multiple images)",
  pillar_r: "🔵 Pilier R - Recul",
  pillar_u: "🔵 Pilier U - Unité",
  pillar_g: "🔵 Pilier G - Gouvernance",
  pillar_i: "🔵 Pilier I - Innovation",
  pillar_r2: "🔵 Pilier R - Rayonnement",
  demarche_1: "📋 Démarche - Étape 1",
  demarche_2: "📋 Démarche - Étape 2",
  demarche_3: "📋 Démarche - Étape 3",
  demarche_4: "📋 Démarche - Étape 4"
}

export function SiteImagesManager({ onClose, embedded }: SiteImagesManagerProps) {
  const [images, setImages] = useState<SiteImage[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    imageUrl: "",
    altText: "",
    caption: "",
    active: true
  })
  const [uploading, setUploading] = useState(false)
  const [inlineCaptions, setInlineCaptions] = useState<Record<string, string>>({})
  const [savingCaption, setSavingCaption] = useState<string | null>(null)

  useEffect(() => {
    loadImages()
  }, [])

  const loadImages = async () => {
    try {
      setLoading(true)
      const result = await lumi.entities.site_images.list({
        sort: { order: 1 }
      })
      setImages(result.list || [])
    } catch (error) {
      console.error("Erreur chargement images:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (section: string, file: File) => {
    try {
      setUploading(true)
      
      const uploadResults = await lumi.tools.file.upload([file])
      
      if (!uploadResults || uploadResults.length === 0) {
        throw new Error("Aucun résultat d'upload reçu")
      }
      
      const uploadResult = uploadResults[0]
      
      if (uploadResult.uploadError) {
        throw new Error(uploadResult.uploadError)
      }
      
      if (!uploadResult.fileUrl) {
        throw new Error("URL du fichier manquante")
      }
      
      const existingImage = images.find(img => img.section === section)
      const isMultiple = isMultipleImagesSection(section)
      
      if (existingImage && !isMultiple) {
        await lumi.entities.site_images.update(existingImage._id, {
          imageUrl: uploadResult.fileUrl,
          updatedAt: new Date().toISOString()
        })
      } else {
        const sectionImages = images.filter(img => img.section === section)
        const maxOrder = sectionImages.reduce((max, img) => Math.max(max, img.order || 0), 0)
        
        await lumi.entities.site_images.create({
          section,
          imageUrl: uploadResult.fileUrl,
          altText: SECTION_LABELS[section],
          caption: "",
          active: true,
          order: maxOrder + 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      }
      
      await loadImages()
      alert("✅ Image uploadée avec succès !")
    } catch (error: any) {
      console.error("Erreur upload:", error)
      const errorMessage = error?.message || error?.data?.error || "Erreur lors de l'upload de l'image"
      alert(`❌ ${errorMessage}`)
    } finally {
      setUploading(false)
    }
  }

  const handleUpdate = async (imageId: string) => {
    try {
      await lumi.entities.site_images.update(imageId, {
        altText: formData.altText,
        caption: formData.caption,
        active: formData.active,
        updatedAt: new Date().toISOString()
      })
      await loadImages()
      setEditingId(null)
      setFormData({ imageUrl: "", altText: "", caption: "", active: true })
    } catch (error) {
      console.error("Erreur mise à jour:", error)
    }
  }

  const toggleActive = async (imageId: string, currentActive: boolean) => {
    try {
      await lumi.entities.site_images.update(imageId, {
        active: !currentActive,
        updatedAt: new Date().toISOString()
      })
      await loadImages()
    } catch (error) {
      console.error("Erreur toggle:", error)
    }
  }

  const handleDelete = async (imageId: string) => {
    if (!confirm("Supprimer cette image ?")) return
    
    try {
      await lumi.entities.site_images.delete(imageId)
      await loadImages()
    } catch (error) {
      console.error("Erreur suppression:", error)
    }
  }

  const handleMoveOrder = async (section: string, imageId: string, direction: 'up' | 'down') => {
    const sectionImages = images
      .filter(img => img.section === section)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
    
    const currentIdx = sectionImages.findIndex(img => img._id === imageId)
    if (currentIdx < 0) return
    
    const swapIdx = direction === 'up' ? currentIdx - 1 : currentIdx + 1
    if (swapIdx < 0 || swapIdx >= sectionImages.length) return
    
    const currentImg = sectionImages[currentIdx]
    const swapImg = sectionImages[swapIdx]
    
    try {
      await Promise.all([
        lumi.entities.site_images.update(currentImg._id, {
          order: swapImg.order || swapIdx + 1,
          updatedAt: new Date().toISOString()
        }),
        lumi.entities.site_images.update(swapImg._id, {
          order: currentImg.order || currentIdx + 1,
          updatedAt: new Date().toISOString()
        })
      ])
      await loadImages()
    } catch (error) {
      console.error("Erreur changement d'ordre:", error)
    }
  }

  const handleSaveCaption = async (imageId: string) => {
    const caption = inlineCaptions[imageId]
    if (caption === undefined) return
    
    try {
      setSavingCaption(imageId)
      await lumi.entities.site_images.update(imageId, {
        caption,
        updatedAt: new Date().toISOString()
      })
      await loadImages()
      setInlineCaptions(prev => {
        const next = { ...prev }
        delete next[imageId]
        return next
      })
      alert("✅ Texte enregistré !")
    } catch (error) {
      console.error("Erreur sauvegarde caption:", error)
      alert("❌ Erreur lors de la sauvegarde du texte")
    } finally {
      setSavingCaption(null)
    }
  }

  const startEdit = (image: SiteImage) => {
    setEditingId(image._id)
    setFormData({
      imageUrl: image.imageUrl,
      altText: image.altText,
      caption: image.caption || "",
      active: image.active
    })
  }

  const groupedImages = images.reduce((acc, img) => {
    if (!acc[img.section]) acc[img.section] = []
    acc[img.section].push(img)
    return acc
  }, {} as Record<string, SiteImage[]>)

  const isMultipleImagesSection = (section: string) => section === 'hero' || section === 'hero_quiz'

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>
  }

  return (
    <div className={embedded ? "" : "bg-white rounded-xl shadow-lg p-8 max-w-6xl mx-auto"}>
      <div className="space-y-8">
        {Object.entries(SECTION_LABELS).map(([section, label]) => {
          const sectionImages = (groupedImages[section] || []).sort((a, b) => (a.order || 0) - (b.order || 0))
          const currentImage = sectionImages[0]
          const isMultiple = isMultipleImagesSection(section)
          const showCaption = section === 'hero' || section === 'hero_quiz'

          return (
            <div key={section} className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {label}
                {isMultiple && <span className="text-sm font-normal text-gray-500 ml-2">(vous pouvez ajouter plusieurs images)</span>}
              </h3>
              {showCaption && (
                <p className="text-sm text-teal-600 mb-4 flex items-center gap-1">
                  <Type className="w-4 h-4" />
                  Chaque image peut avoir un texte associé qui s'affiche dans le slider
                </p>
              )}

              {/* Upload Area */}
              <div className="mb-4">
                <label className="flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl cursor-pointer hover:from-teal-700 hover:to-cyan-700 transition-all">
                  <Upload className="w-5 h-5" />
                  {isMultiple ? (sectionImages.length > 0 ? "Ajouter une image" : "Uploader une image") : (currentImage ? "Remplacer l'image" : "Uploader une image")}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileUpload(section, file)
                    }}
                    disabled={uploading}
                  />
                </label>
              </div>

              {/* Current Image Display */}
              {sectionImages.length > 0 && (
                <div className="space-y-4">
                  {isMultiple && <p className="text-sm text-gray-600 mb-3">📸 {sectionImages.length} image(s) — Utilisez les flèches pour changer l'ordre</p>}
                  
                  {sectionImages.map((img, idx) => (
                    <div key={img._id} className="space-y-3 mb-6 pb-6 border-b border-gray-300 last:border-b-0">
                      {/* Order indicator for multiple */}
                      {isMultiple && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-teal-700 bg-teal-50 px-3 py-1 rounded-full">
                            Position {idx + 1} / {sectionImages.length}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleMoveOrder(section, img._id, 'up')}
                              disabled={idx === 0}
                              className={`p-2 rounded-lg transition-all ${
                                idx === 0
                                  ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                  : "bg-teal-100 text-teal-700 hover:bg-teal-200"
                              }`}
                              title="Monter"
                            >
                              <ArrowUp className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleMoveOrder(section, img._id, 'down')}
                              disabled={idx === sectionImages.length - 1}
                              className={`p-2 rounded-lg transition-all ${
                                idx === sectionImages.length - 1
                                  ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                  : "bg-teal-100 text-teal-700 hover:bg-teal-200"
                              }`}
                              title="Descendre"
                            >
                              <ArrowDown className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Preview */}
                      <div className="relative rounded-lg overflow-hidden border-2 border-gray-300">
                        <img
                          src={img.imageUrl}
                          alt={img.altText}
                          className="w-full h-48 object-cover"
                        />
                        {!img.active && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-white font-bold text-lg">DÉSACTIVÉE</span>
                          </div>
                        )}
                      </div>

                      {/* Caption - always editable inline for hero */}
                      {showCaption && (
                        <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                          <label className="block text-sm font-semibold text-teal-800 mb-2 flex items-center gap-1">
                            <Type className="w-4 h-4" />
                            Texte associé à cette image (affiché dans le slider)
                          </label>
                          <div className="flex gap-2">
                            <textarea
                              value={inlineCaptions[img._id] !== undefined ? inlineCaptions[img._id] : (img.caption || "")}
                              onChange={(e) => setInlineCaptions(prev => ({ ...prev, [img._id]: e.target.value }))}
                              className="flex-1 px-4 py-2 border border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none text-sm bg-white"
                              rows={2}
                              placeholder="Ex: Accompagnement personnalisé pour les dirigeants..."
                            />
                            <button
                              onClick={() => handleSaveCaption(img._id)}
                              disabled={savingCaption === img._id}
                              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all flex items-center gap-1 self-end disabled:opacity-50"
                            >
                              <Save className="w-4 h-4" />
                              {savingCaption === img._id ? "..." : "OK"}
                            </button>
                          </div>
                          {img.caption && (
                            <p className="text-xs text-teal-600 mt-1">✅ Texte actuel enregistré</p>
                          )}
                        </div>
                      )}

                      {/* Edit Form */}
                      {editingId === img._id ? (
                        <div className="space-y-3 bg-white p-4 rounded-lg border border-gray-300">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Texte alternatif
                            </label>
                            <input
                              type="text"
                              value={formData.altText}
                              onChange={(e) => setFormData({ ...formData, altText: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                            />
                          </div>

                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.active}
                              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                              className="w-4 h-4"
                            />
                            <label className="text-sm font-medium text-gray-700">Image active</label>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleUpdate(img._id)}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                              <Save className="w-4 h-4" />
                              Enregistrer
                            </button>
                            <button
                              onClick={() => {
                                setEditingId(null)
                                setFormData({ imageUrl: "", altText: "", caption: "", active: true })
                              }}
                              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-300">
                          <div className="flex-1">
                            <p className="text-sm text-gray-700">
                              <strong>Alt:</strong> {img.altText}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Mis à jour: {new Date(img.updatedAt).toLocaleDateString()}
                            </p>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleActive(img._id, img.active)}
                              className={`p-2 rounded-lg ${
                                img.active
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                              }`}
                              title={img.active ? "Désactiver" : "Activer"}
                            >
                              {img.active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                            </button>
                            <button
                              onClick={() => startEdit(img)}
                              className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                              title="Modifier"
                            >
                              <Save className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(img._id)}
                              className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                              title="Supprimer"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {sectionImages.length === 0 && (
                <p className="text-gray-500 text-center py-4">Aucune image uploadée pour cette section</p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
